<template>
  <section class="rounded-lg border bg-card px-6 py-5">
    <div class="flex items-center justify-between gap-3">
      <h3 class="font-mono text-xs uppercase tracking-wide text-muted-foreground">Task Actions</h3>
      <TaskFeatureDropdown
        :task="task"
        :context="context"
        :readonly="readonly"
        :is-creating-checklist="isCreatingChecklist"
        @feature-enabled="enableFeature"
        @feature-disabled="disableFeature"
        @create-checklist="emit('create-checklist')"
      />
    </div>
    <div class="mt-4 flex flex-wrap items-center gap-2.5">
      <TaskStatusField :value="task.status" show-label />
      <TaskPriorityField v-if="showPriorityField" :task="task" :value="task.priority" :context="context" show-label />
      <TaskStartAtField v-if="showStartAtField" :task="task" :value="task.start_at" :context="context" show-label />
      <TaskDeadlineField v-if="showDeadlineField" :task="task" :value="task.deadline_at" :context="context" show-label />
      <TaskAssigneeField v-if="showAssigneeField" :task="task" :value="task.assigned_to_user_id" :context="context" show-label />
      <ReviewProcessSetupCard
        v-if="showReviewProcessField"
        :review-process-id="task.review_process?.id ?? task.reviewProcess?.id ?? null"
        :reviewable-type="task.object_type"
        :reviewable-id="task.id"
        mode="compact"
        button-only
        @updated="onReviewProcessChanged"
        @created="onReviewProcessChanged"
        @removed="onReviewProcessChanged"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import TaskAssigneeField from './fields/TaskAssigneeField.vue'
import TaskDeadlineField from './fields/TaskDeadlineField.vue'
import TaskFeatureDropdown from './TaskFeatureDropdown.vue'
import TaskPriorityField from './fields/TaskPriorityField.vue'
import TaskStartAtField from './fields/TaskStartAtField.vue'
import TaskStatusField from './fields/TaskStatusField.vue'
import ReviewProcessSetupCard from '../../../../../review-process/resources/js/components/review-process/ReviewProcessSetupCard.vue'

const emit = defineEmits(['create-checklist', 'review-process-updated', 'tags-enabled'])

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  context: {
    type: String,
    default: 'task-show',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  isCreatingChecklist: {
    type: Boolean,
    default: false,
  },
})

const enabledFeatures = ref([])

const normalizedPriority = computed(() => {
  if (typeof props.task?.priority === 'string') {
    return props.task.priority.trim()
  }

  if (props.task?.priority && typeof props.task.priority === 'object' && 'value' in props.task.priority) {
    return String(props.task.priority.value).trim()
  }

  return ''
})
const hasPriority = computed(() => normalizedPriority.value !== '' && normalizedPriority.value !== 'standard')
const hasStartAt = computed(() => hasValue(props.task?.start_at))
const hasDeadline = computed(() => hasValue(props.task?.deadline_at))
const hasAssignee = computed(() => {
  const assigneeId = Number(props.task?.assigned_to_user_id)

  return Number.isInteger(assigneeId) && assigneeId > 0
})
const hasReviewProcess = computed(() => {
  const reviewProcessId = Number(props.task?.review_process?.id ?? props.task?.reviewProcess?.id ?? 0)

  return Number.isInteger(reviewProcessId) && reviewProcessId > 0
})

const showPriorityField = computed(() => shouldShowFeature('priority', hasPriority.value))
const showStartAtField = computed(() => shouldShowFeature('startAt', hasStartAt.value))
const showDeadlineField = computed(() => shouldShowFeature('deadline', hasDeadline.value))
const showAssigneeField = computed(() => shouldShowFeature('assignee', hasAssignee.value))
const showReviewProcessField = computed(() => shouldShowFeature('review', hasReviewProcess.value))

const enableFeature = (feature) => {
  if (enabledFeatures.value.includes(feature)) {
    return
  }

  enabledFeatures.value = [...enabledFeatures.value, feature]

  if (feature === 'tags') {
    emit('tags-enabled')
  }
}

const disableFeature = (feature) => {
  enabledFeatures.value = enabledFeatures.value.filter((enabledFeature) => enabledFeature !== feature)
}

const shouldShowFeature = (feature, hasConfiguredValue) => {
  return !props.readonly && (hasConfiguredValue || enabledFeatures.value.includes(feature))
}

const onReviewProcessChanged = (payload) => {
  emit('review-process-updated', payload)
}

const hasValue = (value) => {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'string') {
    return value.trim() !== ''
  }

  return true
}

watch(
  () => props.task?.id,
  () => {
    enabledFeatures.value = []
  },
)

</script>
